import { VoiceButton } from "../../voice/VoiceSurface"
import NiceModal, { useModal } from "@ebay/nice-modal-react"
import cx from "classnames"
import { BarChart3, Search, Settings, SquarePen } from "lucide-react"
import { observer } from "mobx-react"
import { useCallback, useEffect } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { useParams } from "react-router"
import { Z_INDEX } from "../../constants"
import { useComputers } from "../../computers/context"
import { useMediaQuery } from "../../hooks/useMediaQuery"
import { useCodeNavigate } from "../../routing"
import { useCodeStore } from "../../store/context"
import { GuestBanner } from "../computers/GuestBanner"
import { SettingsModal, type SettingsTab } from "../settings/SettingsModal"
import { TaskSearchModal } from "../search/TaskSearchModal"
import { ReposSidebarContent } from "./RepoList"
import { MergedTasksSidebarContent } from "./TaskList"
import { installQueueModeLifecycle } from "./queueModeLifecycle"
import { queueModeManager } from "./queueModeManager"
import { codeSidebarManager } from "./sidebarManager"
import SidebarIcon from "./static/sidebar.svg?react"

const ELECTRON_DRAG_REGION_CLASS = "electron-drag-region"
const ELECTRON_NO_DRAG_REGION_CLASS = "electron-no-drag-region"

const SidebarContent = observer(() => {
    const manager = codeSidebarManager
    const codeStore = useCodeStore()
    const computers = useComputers()
    const navigate = useCodeNavigate()
    const params = useParams<{ workspaceId?: string; taskId?: string; creationId?: string }>()
    const { workspaceId, taskId, creationId } = params
    const settingsShortcut = /Mac|iPhone|iPad|iPod/.test(navigator.platform) ? "⌘," : "Ctrl+,"
    const taskSearchModal = useModal(TaskSearchModal)
    useEffect(() => installQueueModeLifecycle(queueModeManager), [])
    const handleOpenSettings = useCallback(
        (tab?: SettingsTab) => {
            NiceModal.show(SettingsModal, { store: codeStore, initialTab: tab })
        },
        [codeStore]
    )

    useHotkeys(
        "meta+comma,ctrl+comma",
        (event) => {
            if (!event.repeat) handleOpenSettings()
        },
        { preventDefault: true, enableOnContentEditable: true, enableOnFormTags: true },
        [handleOpenSettings]
    )

    useHotkeys(
        "meta+k,ctrl+k,meta+f,ctrl+f",
        (event) => {
            event.preventDefault()
            if (!event.repeat) taskSearchModal.show()
        },
        { enableOnContentEditable: true, enableOnFormTags: true },
        [taskSearchModal]
    )

    return (
        <>
            <div className={`${ELECTRON_DRAG_REGION_CLASS} flex h-11 flex-shrink-0 items-center px-3`}>
                <div className="ml-auto flex gap-1">
                    <button
                        type="button"
                        className={`${ELECTRON_NO_DRAG_REGION_CLASS} btn flex h-8 w-8 items-center justify-center text-muted hover:bg-base-300/60 hover:text-base-content max-[900px]:h-11 max-[900px]:w-11`}
                        onClick={() => taskSearchModal.show()}
                        title="Search tasks on all computers"
                        aria-label="Search tasks"
                    >
                        <Search className="h-4 w-4" />
                    </button>
                    {!codeStore.isGuest ? (
                        <button
                            type="button"
                            className={`${ELECTRON_NO_DRAG_REGION_CLASS} btn flex h-8 w-8 items-center justify-center text-muted hover:bg-base-300/60 hover:text-base-content disabled:cursor-default disabled:opacity-40 max-[900px]:h-11 max-[900px]:w-11`}
                            onClick={() => {
                                if (!workspaceId) return
                                navigate.openTaskCreate(workspaceId)
                                manager.closeSidebarForNavigation()
                            }}
                            disabled={!workspaceId}
                            title={workspaceId ? "New task in the current workspace" : "Select a workspace to create a task"}
                            aria-label="New task"
                        >
                            <SquarePen className="h-4 w-4" />
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className={`${ELECTRON_NO_DRAG_REGION_CLASS} btn flex h-8 w-8 items-center justify-center text-muted hover:bg-base-300/60 hover:text-base-content max-[900px]:h-11 max-[900px]:w-11`}
                        onClick={manager.toggleSidebar}
                        title="Close sidebar"
                        aria-label="Close sidebar"
                    >
                        <SidebarIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>
            <GuestBanner
                onManage={() => {
                    manager.closeSidebarForNavigation()
                    handleOpenSettings("connections")
                }}
            />
            <div className="min-h-0 flex-1">
                <ReposSidebarContent
                    key={computers.scope}
                    storageScope={computers.scope}
                    workspaceId={workspaceId}
                    renderMergedContent={(repos, workspaceColorById, remoteQueues) => (
                        <>
                            <MergedTasksSidebarContent
                                workspaces={repos.map((repo) => ({
                                    id: repo.id,
                                    name: repo.name,
                                    color: workspaceColorById.get(repo.id),
                                    glyph: repo.glyph,
                                    projectChars: repo.projectChars,
                                    projectEmoji: repo.projectEmoji,
                                }))}
                                remoteQueues={remoteQueues}
                                activeWorkspaceId={workspaceId}
                                taskId={taskId}
                                creationId={creationId}
                            />
                        </>
                    )}
                />
            </div>
            <div className="flex flex-shrink-0 items-center gap-1 border-t border-border/60 px-2 pt-1.5 pb-[max(env(safe-area-inset-bottom),0.375rem)]">
                <button
                    type="button"
                    className="btn flex min-h-8 min-w-0 flex-1 items-center gap-2 px-2 text-xs text-base-content/75 transition-colors hover:bg-base-300/60 hover:text-base-content focus-visible:bg-base-300/60 focus-visible:text-base-content max-[900px]:min-h-11"
                    onClick={() => handleOpenSettings()}
                    title={`Settings (${settingsShortcut})`}
                    aria-label="Settings"
                    aria-keyshortcuts="Meta+, Control+,"
                >
                    <Settings className="h-3.5 w-3.5 flex-none" />
                    <span>Settings</span>
                    <kbd aria-hidden="true" className="ml-auto hidden font-sans text-[10px] text-muted/60 min-[901px]:inline">
                        {settingsShortcut}
                    </kbd>
                </button>
                <VoiceButton />
                <span aria-hidden="true" className="h-4 w-px flex-none bg-border/60" />
                <button
                    type="button"
                    className="btn flex h-8 w-8 flex-none items-center justify-center text-muted transition-colors hover:bg-base-300/60 hover:text-base-content focus-visible:bg-base-300/60 focus-visible:text-base-content max-[900px]:h-11 max-[900px]:w-11"
                    onClick={() => handleOpenSettings("stats")}
                    title="Usage stats"
                    aria-label="Usage stats"
                >
                    <BarChart3 className="h-3.5 w-3.5" />
                </button>
            </div>
        </>
    )
})

const BaseSidebar = observer(() => {
    const manager = codeSidebarManager
    const hidden = !manager.showSidebar

    const containerClasses = cx(
        "absolute left-0 top-0 flex flex-col bg-base-200 flex-shrink-0 select-none transition-[transform,opacity] duration-300 ease-in-out overflow-hidden h-full w-[300px]",
        hidden ? "invisible -translate-x-4 opacity-0 pointer-events-none" : "visible translate-x-0 opacity-100"
    )

    const contentClasses = cx("flex flex-col h-full min-w-[300px] transition-opacity duration-300", hidden ? "opacity-0" : "opacity-100")

    return (
        <div className={containerClasses} aria-hidden={hidden}>
            <div className={contentClasses}>
                <SidebarContent />
            </div>
        </div>
    )
})

const DrawerBaseSidebar = observer(() => {
    const manager = codeSidebarManager
    const hidden = !manager.showSidebar

    const containerClasses = cx(
        "absolute left-0 top-0 h-full select-none border-r border-border bg-base-200 transition-transform duration-300",
        "w-[min(340px,calc(100vw-20px))]",
        hidden ? "invisible -translate-x-full" : "visible translate-x-0"
    )

    const innerClasses = "flex h-full flex-col pt-[max(env(safe-area-inset-top),0.5rem)]"

    return (
        <>
            {/* Backdrop */}
            {!hidden && (
                <button
                    type="button"
                    className="btn absolute inset-0 bg-black/15"
                    style={{ zIndex: Z_INDEX.MOBILE_SIDEBAR_BACKDROP }}
                    onClick={manager.toggleSidebar}
                    aria-label="Close sidebar"
                />
            )}

            <div className={containerClasses} style={{ zIndex: Z_INDEX.MOBILE_SIDEBAR }} aria-hidden={hidden}>
                <div className={innerClasses}>
                    <SidebarContent />
                </div>
            </div>
        </>
    )
})

export const CodeSidebar = observer(() => {
    const drawer = useMediaQuery("(max-width: 900px)")
    return drawer ? <DrawerBaseSidebar /> : <BaseSidebar />
})
